import React from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import { SpacingProps } from '../box';
import { spacingNames, spacing } from '../../style/spacing';
import { withSpacing } from '../../utils';
import fonts from '../../style/fonts';
import {
  CssUnitsAsResponsiveObject,
  getCssRuleWithResponsiveCalculation,
} from '../../utils/get-css-rule-with-responsive-calc';
import { types, variants, sizes } from './style';

type TitleFontFamily = keyof typeof fonts;

export type Weights = 900 | 700 | 600 | 500 | 400 | 300 | 'inherit';

export interface TitleProps extends SpacingProps {
  componentClass?: keyof typeof types | 'span' | 'label' | 'p';
  className?: string;
  variant?: keyof typeof variants;
  onClick?: () => void;
  capitalizeFirstLetter?: boolean;
  'data-name'?: string;
  'data-value'?: string;
  clickable?: boolean;
  children?: any;
  style?: React.CSSProperties;
  id?: string;
  htmlFor?: string;
  weight?: Weights;
  align?: CssUnitsAsResponsiveObject<'left' | 'right' | 'center' | 'start' | 'end'>;
  display?: 'block' | 'flex' | 'inline-block' | 'inline';
  wordBreak?: React.CSSProperties['wordBreak'];
  whiteSpace?: 'pre-line' | 'break-spaces' | 'nowrap' | 'pre-wrap';
  decoration?: React.CSSProperties['textDecoration'];
  textTransform?: 'lowercase' | 'uppercase' | 'capitalize';
  fontStyle?: 'italic' | 'underline';
  flex?: boolean;
  alignItems?: 'flex-start' | 'center' | 'flex-end';
  size?: keyof typeof sizes;
  'data-testid'?: string;
  gap?: keyof typeof spacing;
  dir?: 'ltr' | 'rtl' | 'auto';
  fontFamily?: TitleFontFamily;
  letterSpacing?: React.CSSProperties['letterSpacing'];
  lineClamp?: number;
  textWrap?: React.CSSProperties['textWrap'];
}

export const TitleComponent = ({
  componentClass: TagName = 'h3',
  className,
  onClick,
  capitalizeFirstLetter,
  'data-name': dataName,
  'data-testid': dataTestId,
  'data-value': dataValue,
  clickable,
  children,
  style,
  id,
  gap,
  ...rest
}: TitleProps) => (
  <TagName
    style={style}
    data-name={dataName}
    data-value={dataValue}
    onClick={onClick}
    id={id}
    className={classNames(
      'box-title',
      className,
      { 'capitalize-first-letter': capitalizeFirstLetter },
      `box-${TagName}`,
      { clickable: onClick || clickable },
    )}
    data-testid={dataTestId}
    {...rest}
  >
    {children}
  </TagName>
);

const Title = styled(TitleComponent).withConfig({
  shouldForwardProp: prop =>
    ![
      ...spacingNames,
      'weight',
      'display',
      'clickable',
      'whiteSpace',
      'align',
      'decoration',
      'wordBreak',
      'textTransform',
      'fontStyle',
      'display',
      'flex',
      'alignItems',
      'variant',
      'size',
      'fontFamily',
      'letterSpacing',
      'lineClamp',
      'textWrap',
    ].includes(prop),
})<TitleProps>`
  ${withSpacing};
  display: block;
  white-space: ${props => props.whiteSpace};
  font-weight: ${props => props.weight};
  text-decoration: ${props => props.decoration};
  word-break: ${props => props.wordBreak};
  text-transform: ${props => props.textTransform};
  font-style: ${props => props.fontStyle};
  ${props =>
    getCssRuleWithResponsiveCalculation<TitleProps['align']>({
      property: 'text-align',
      value: props.align || 'start',
    })};
  ${props =>
    props.gap &&
    css`
      gap: ${spacing[props.gap]};
    `}
  ${props =>
    props.display &&
    css`
      display: ${props.display};
    `};

  ${props =>
    props.flex &&
    css`
      display: flex;
    `};

  ${props =>
    props.alignItems &&
    css`
      align-items: ${props.alignItems};
    `};

  ${props => props.componentClass && types[props.componentClass]}
  ${props => variants[props.variant || 'gray900']};
  ${props =>
    props.fontFamily &&
    css`
      font-family: ${fonts[props.fontFamily]};
    `};
  ${props =>
    props.letterSpacing !== undefined &&
    css`
      letter-spacing: ${props.letterSpacing};
    `};
  ${({ lineClamp }) =>
    lineClamp &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: ${lineClamp};
      -webkit-box-orient: vertical;
      overflow: hidden;
    `};

  ${({ textWrap }) => css`
    text-wrap: ${textWrap};
  `}

  -webkit-font-smoothing: antialiased;
`;

export const H1: React.FC<TitleProps> = ({ componentClass = 'h1', weight = 500, ...rest }: TitleProps) => (
  <Title {...rest} weight={weight} componentClass={componentClass} />
);
export const H2: React.FC<TitleProps> = ({ componentClass = 'h2', weight = 500, ...rest }: TitleProps) => (
  <Title {...rest} weight={weight} componentClass={componentClass} />
);
export const H3: React.FC<TitleProps> = ({ componentClass = 'h3', weight = 500, ...rest }: TitleProps) => (
  <Title {...rest} weight={weight} componentClass={componentClass} />
);
export const H4: React.FC<TitleProps> = ({ componentClass = 'h4', weight = 500, ...rest }: TitleProps) => (
  <Title {...rest} weight={weight} componentClass={componentClass} />
);
export const H5: React.FC<TitleProps> = ({ componentClass = 'h5', weight = 500, ...rest }: TitleProps) => {
  return <Title {...rest} weight={weight} componentClass={componentClass} />;
};
export const H6: React.FC<TitleProps> = ({ componentClass = 'h6', weight = 500, ...rest }: TitleProps) => (
  <Title {...rest} weight={weight} componentClass={componentClass} />
);

export { Title };
export default Title;
